/**
 * `GET /journeys` · `GET /journeys/{id}` · أفعال الخطوة.
 *
 * 🔴 **ليه الشاشة دي موجودة:** السيستم كله مبني على الرحلة، والرحلة الواقفة
 *    كانت **غير مرئية لأي حد** — الفريق يشوف الطابور بيتراكم ومش عارف
 *    السبب، ومدير الأكاديمية مالوش أي صورة. محاكاة حيّة أثبتت إن رحلة
 *    بتقف عند خطوة ٤ من ٨ ومحدش يعرف.
 *
 * ⚠️ العزل من السيرفر: `scope_filter` بيتحقن في كل استعلام، فالعضو بيشوف
 *    رحلات أقسامه بس. الواجهة **ما بتفلترش** — بتعرض اللي وصلها.
 */
import { ApiError, api } from "./client";
import { latinDigits } from "./registry";

function fail(path: string, reason: string): never {
  console.error(`[journeys] ${reason} (${path})`);
  throw new ApiError(
    502,
    { code: "EMPTY_RESPONSE", message_ar: "السيستم رجّع رد مش مفهوم — مقدرناش نعرض البيانات" },
    null,
  );
}

/**
 * ⚠️ **نقطة فرض قاعدة الأرقام اللاتينية** — نفس نمط `api/registry.ts`.
 *    كل نص جاي من السيستم بيعدّي من هنا، فأي رقم عربي-هندي (٠١٢٣) في نص
 *    عربي بيتحوّل للاتيني قبل ما يوصل الشاشة.
 * ⛔ ما تستبدلهاش بـ`String(v)` — القاعدة بتتكسر بصمت.
 */
function str(v: unknown): string {
  return typeof v === "string" ? latinDigits(v) : "";
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

export type JourneyStepRow = {
  step_no: number;
  title_ar: string;
  state: string;
  state_label_ar: string;
  owner_dept: string | null;
  owner_dept_ar: string;
  auto: boolean;
  sla_label_ar: string;
  /** الناقص لإقفال الخطوة — بالعربي زي ما المحرّك بيرجّعه */
  missing_ar: string;
  assignee: { id: string; name_ar: string } | null;
  /**
   * 🔑 **الفعل الواحد** اللي السيرفر حسبه للي بيبص:
   *   `close` المهمة معاك أو في طابور قسمك · `nudge` عند حد تاني ·
   *   `null` مقفولة أو لسه ما جاش دورها. الشاشة ما بتخمّنش.
   */
  action: StepAction;
};

export type StepAction = "close" | "nudge" | null;

/**
 * 🔴 «الناقص» كان **مكتوب ومش بيتعرض أبدًا**: السيرفر بيرجّعه قائمة، والمحوّل
 *    كان بيقرا نصًّا بس ⇒ القائمة بتتحوّل لفاضي والشاشة تسكت. الموظفة كانت
 *    تضغط تقفل وتاخد «فيه بنود ناقصة» من غير ما تعرف إيه.
 */
function missingAr(v: unknown): string {
  if (Array.isArray(v)) return v.filter((x) => typeof x === "string").map(latinDigits).join(" · ");
  return str(v);
}

function stepAction(v: unknown): StepAction {
  return v === "close" || v === "nudge" ? v : null;
}

export type JourneyRow = {
  id: string;
  template_key: string;
  template_label_ar: string;
  subject_label_ar: string;
  subject_type: string;
  subject_ref: string;
  status: string;
  status_label_ar: string;
  current_step_no: number | null;
  current_step_title_ar: string;
  current_dept: string | null;
  current_dept_ar: string;
  /** 🔑 فين بيتكتب الناقص: رابط سجل الطالب/الأسرة أو الطلب — من السيرفر، مش تخمين */
  subject_href: string | null;
  /** «واقفة من ٣ أيام» — بيتحسب في السيرفر بنفس تعريف لوحة المدير */
  still_label_ar: string;
  blocked_summary: string;
  owner: { id: string; name_ar: string } | null;
  summary: { completed: number; total: number };
  version: number;
  steps?: JourneyStepRow[];
};

export type JourneyListMeta = {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  /** بعد كام ساعة نعتبرها «واقفة» — من إعدادات السيستم مش رقم في الشاشة */
  stallHours: number;
  searchedFields: string[];
};

function person(raw: unknown): { id: string; name_ar: string } | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const id = str(r["id"]);
  return id ? { id, name_ar: str(r["name_ar"]) } : null;
}

function step(raw: unknown): JourneyStepRow {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    step_no: num(r["step_no"]) ?? 0,
    title_ar: str(r["title_ar"]),
    state: str(r["state"]),
    state_label_ar: str(r["state_label_ar"]),
    owner_dept: str(r["owner_dept"]) || null,
    owner_dept_ar: str(r["owner_dept_ar"]),
    auto: Boolean(r["auto"]),
    sla_label_ar: str(r["sla_label_ar"]),
    missing_ar: missingAr(r["missing_ar"]),
    assignee: person(r["assignee"]),
    action: stepAction(r["action"]),
  };
}

function row(raw: unknown): JourneyRow {
  const r = (raw ?? {}) as Record<string, unknown>;
  const s = (r["summary"] ?? {}) as Record<string, unknown>;
  const steps = Array.isArray(r["steps"]) ? (r["steps"] as unknown[]).map(step) : undefined;
  return {
    id: str(r["id"]),
    template_key: str(r["template_key"]),
    template_label_ar: str(r["template_label_ar"]),
    subject_label_ar: str(r["subject_label_ar"]),
    subject_type: str(r["subject_type"]),
    subject_ref: str(r["subject_ref"]),
    status: str(r["status"]),
    status_label_ar: str(r["status_label_ar"]),
    current_step_no: num(r["current_step_no"]),
    current_step_title_ar: str(r["current_step_title_ar"]),
    current_dept: str(r["current_dept"]) || null,
    current_dept_ar: str(r["current_dept_ar"]),
    subject_href: str(r["subject_href"]) || null,
    still_label_ar: str(r["still_label_ar"]),
    blocked_summary: str(r["blocked_summary"]),
    owner: person(r["owner"]),
    summary: { completed: num(s["completed"]) ?? 0, total: num(s["total"]) ?? 0 },
    version: num(r["version"]) ?? 1,
    steps,
  };
}

export type ListJourneysOpts = {
  q?: string;
  status?: string;
  dept?: string;
  templateKey?: string;
  /** 🔑 السؤال اللي الشاشة موجودة عشانه: «مين واقف وعند مين؟» */
  stuck?: boolean;
  page?: number;
  perPage?: number;
};

export async function listJourneys(
  opts: ListJourneysOpts = {},
): Promise<{ rows: JourneyRow[]; meta: JourneyListMeta }> {
  const path = "/journeys";
  const res = await api.get<unknown>(path, {
    query: {
      q: opts.q || undefined,
      status: opts.status || undefined,
      dept: opts.dept || undefined,
      template_key: opts.templateKey || undefined,
      stuck: opts.stuck ? "true" : undefined,
      page: opts.page ?? 1,
      per_page: opts.perPage ?? 25,
    },
  });
  const data = res.data;
  if (!Array.isArray(data)) fail(path, "قايمة الرحلات رجعت بشكل مش متوقع");
  const m = (res.meta ?? {}) as Record<string, unknown>;
  return {
    rows: (data as unknown[]).map(row),
    meta: {
      page: num(m["page"]) ?? 1,
      perPage: num(m["per_page"]) ?? 25,
      total: num(m["total"]) ?? 0,
      totalPages: num(m["total_pages"]) ?? 1,
      stallHours: num(m["stall_hours"]) ?? 0,
      searchedFields: Array.isArray(m["searched_fields"])
        ? (m["searched_fields"] as unknown[]).map(str)
        : [],
    },
  };
}

export async function getJourney(id: string): Promise<JourneyRow> {
  const path = `/journeys/${id}`;
  const res = await api.get<unknown>(path);
  if (!res.data || typeof res.data !== "object") fail(path, "الرحلة رجعت فاضية");
  return row(res.data);
}

/** «ذكّر القسم» — الفعل المتاح على خطوة مفتوحة عند غيرك */
export async function nudgeStep(id: string, stepNo: number): Promise<string> {
  const res = await api.post<unknown>(`/journeys/${id}/steps/${stepNo}/nudge`);
  return str((res as Record<string, unknown>)["toast_ar"]) || "التذكير اتبعت";
}

/**
 * «اقفل الخطوة» من ملف الرحلة — بسطر دليل، والسيرفر بيستلم مهمتها ويقفلها.
 * ⛔ بلا سطر ⇒ `CLOSE_EVIDENCE_REQUIRED` · بيانات ناقصة ⇒
 *    `COMPLETION_CONDITION_NOT_MET` — الاتنين بيتقالوا للموظفة زي ما هما.
 */
export async function completeStep(id: string, stepNo: number, noteAr: string): Promise<string> {
  const res = await api.post<unknown>(`/journeys/${id}/steps/${stepNo}/complete`, {
    note_ar: noteAr.trim() || null,
  });
  return str((res as Record<string, unknown>)["toast_ar"]) || "اتقفلت الخطوة";
}

/** «أوقف الرحلة» — بسبب مكتوب، ومهامها بتتقفل بالسبب ده */
export async function pauseJourney(id: string, reasonAr: string): Promise<string> {
  const res = await api.post<unknown>(`/journeys/${id}/hold`, { reason_ar: reasonAr.trim() });
  return str((res as Record<string, unknown>)["toast_ar"]) || "الرحلة اتوقفت مؤقتًا";
}

/** «كمّل الرحلة» — بيولّد مهمة الخطوة الحالية من جديد */
export async function resumeJourney(id: string): Promise<string> {
  const res = await api.post<unknown>(`/journeys/${id}/resume`);
  return str((res as Record<string, unknown>)["toast_ar"]) || "الرحلة كمّلت";
}

/** تخطّي خطوة — **بسبب إجباري**، وللمدير بس (السيرفر بيفرض الاتنين) */
export async function skipStep(
  id: string,
  stepNo: number,
  reasonAr: string,
  version: number,
): Promise<string> {
  const res = await api.post<unknown>(
    `/journeys/${id}/steps/${stepNo}/skip`,
    { reason_ar: reasonAr },
    { ifMatch: version },
  );
  return str((res as Record<string, unknown>)["toast_ar"]) || "الخطوة اتخطّت";
}
